import { useCallback, useEffect, useRef, useState } from "react";
import AdminService from "../AdminService";

/**
 * useManageQueue
 *
 * Manages all state and data fetching for the Manage Queue screen.
 * Supports server-side search (debounced), sorting, and pagination.
 *
 * @param {object}  [opts]
 * @param {string}  [opts.searchTenantId]  - When provided, fetches queues for that tenant
 *                                           (cross-tenant admin view from Manage Customers).
 *                                           Mirrors the searchUsersTenantId pattern.
 */
export function useManageQueue({ searchTenantId } = {}) {
    // ── Queue list state ───────────────────────────────────────────────────
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [tenantInfo, setTenantInfo] = useState(null);
    const [rowSelection, setRowSelectionRaw] = useState({});
    const [isGlobalSelectAll, setIsGlobalSelectAll] = useState(false);
    const [globalFilter, setGlobalFilter] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [sorting, setSortingState] = useState([]); // TanStack format: [{ id, desc }]
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
    const isFetchingRef = useRef(false);
    const isFirstRenderRef = useRef(true);

    // ── Unlink confirmation modal state ──────────────────────────────────────
    const [unlinkTarget, setUnlinkTarget] = useState(null);
    const [unlinking, setUnlinking] = useState(false);

    // ── Bulk unlink state ──────────────────────────────────────────────────
    const [bulkUnlinkOpen, setBulkUnlinkOpen] = useState(false);
    const [bulkUnlinking, setBulkUnlinking] = useState(false);
    const [bulkUnlinkIsSelectAll, setBulkUnlinkIsSelectAll] = useState(false);
    const [bulkUnlinkQueueIds, setBulkUnlinkQueueIds] = useState([]);
    const [bulkUnlinkFilterSearch, setBulkUnlinkFilterSearch] = useState("");
    const [bulkUnlinkSelectedCount, setBulkUnlinkSelectedCount] = useState(0);
    const [bulkUnlinkLinkedCount, setBulkUnlinkLinkedCount] = useState(0);

    // ── Sync Queues state ──────────────────────────────────────────────────
    const [syncing, setSyncing] = useState(false);

    // ── Link drawer state ──────────────────────────────────────────────────
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerOrgs, setDrawerOrgs] = useState([]);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [drawerQueues, setDrawerQueues] = useState([]);
    const [drawerIsSelectAll, setDrawerIsSelectAll] = useState(false);
    const [drawerFilterSearch, setDrawerFilterSearch] = useState("");
    const [linking, setLinking] = useState(false);
    const [linkError, setLinkError] = useState(null);

    // Wrap setRowSelection so clearing selection also clears global-select-all
    const setRowSelection = useCallback((updater) => {
        setRowSelectionRaw(updater);
        setIsGlobalSelectAll(false);
    }, []);

    // ── Debounce globalFilter → debouncedSearch (900ms, matches UsersTable) ─
    useEffect(() => {
        // Skip on initial mount — debouncedSearch is already initialised to ""
        // and resetting pagination creates a new object reference that would
        // re-create fetchQueues and fire a redundant second API call.
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            return;
        }
        const timer = setTimeout(() => {
            setDebouncedSearch(globalFilter);
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }, 900);
        return () => clearTimeout(timer);
    }, [globalFilter]);

    // ── Server-side data fetch ────────────────────────────────────────────
    const fetchQueues = useCallback(async () => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        setLoading(true);
        setError(null);
        // Reset global-select-all on every new fetch (page/sort/search change)
        setIsGlobalSelectAll(false);
        setRowSelectionRaw({});

        try {
            const sort = sorting?.[0];
            const res = await AdminService.getQueuesData({
                page: pagination.pageIndex + 1,
                pageSize: pagination.pageSize,
                search: debouncedSearch || undefined,
                sortBy: sort?.id,
                sortOrder: sort ? (sort.desc ? "desc" : "asc") : undefined,
                searchTenantId: searchTenantId || undefined,
            });
            setData(res?.data ?? []);
            setTotalCount(res?.pagination?.total ?? 0);
            if (res?.tenant) setTenantInfo(res.tenant);
        } catch (err) {
            console.error("[ManageQueue] API error:", err);
            setError(err);
            setData([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [pagination, sorting, debouncedSearch, searchTenantId]);

    useEffect(() => {
        fetchQueues();
    }, [fetchQueues]);

    // ── Sorting handler — resets to page 1 on every sort change ──────────
    const setSorting = useCallback((updater) => {
        setSortingState(updater);
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, []);

    /** Opens the unlink confirmation modal for the given queue row. */
    const handleUnlinkRequest = useCallback((queue) => {
        setUnlinkTarget(queue);
    }, []);

    /** Cancels the unlink — closes the modal without doing anything. */
    const handleUnlinkCancel = useCallback(() => {
        setUnlinkTarget(null);
    }, []);

    /**
     * Confirmed unlink — calls the API then removes the link from local state
     * so the table updates instantly without a refetch.
     * @param {object} [callbacks]
     * @param {function} [callbacks.onSuccess]
     * @param {function} [callbacks.onError]
     */
    const handleUnlinkConfirm = useCallback(async ({ onSuccess, onError } = {}) => {
        if (!unlinkTarget) return;
        setUnlinking(true);
        try {
            await AdminService.unlinkSalesforceOrg(unlinkTarget.queueId, searchTenantId || undefined);

            // Optimistic update — clear linked org AND missed-call owner
            // fields on the row (both are cleared server-side too).
            setData((prev) =>
                prev.map((q) =>
                    q.queueId === unlinkTarget.queueId
                        ? {
                              ...q,
                              linkedSalesforceOrgId:       null,
                              linkedSalesforceInstanceUrl: null,
                              missedCallOwnerId:           null,
                              missedCallOwnerUsername:     null,
                              missedCallOwnerSfUserId:     null,
                          }
                        : q,
                ),
            );
            setUnlinkTarget(null);
            onSuccess?.();
        } catch (err) {
            console.error("[ManageQueue] Unlink failed:", err);
            onError?.(err);
        } finally {
            setUnlinking(false);
        }
    }, [unlinkTarget, searchTenantId]);

    // ── Link drawer handlers ───────────────────────────────────────────────

    /**
     * Fetches available Salesforce orgs for the link drawer.
     * - Cross-tenant (searchTenantId provided): calls getSFOrgsOfTenant so only
     *   orgs belonging to the target tenant are offered for linking.
     * - Session-tenant (no searchTenantId): calls getSFConnection to get the
     *   logged-in tenant's own orgs.
     */
    const fetchOrgsForDrawer = useCallback(async () => {
        setDrawerLoading(true);
        try {
            let orgs;
            if (searchTenantId) {
                // Cross-tenant view — fetch orgs for the specific tenant being managed
                const res = await AdminService.getSFOrgsOfTenant(searchTenantId);
                orgs = Array.isArray(res?.data) ? res.data : [];
            } else {
                // Session-tenant view — fetch orgs for the logged-in tenant
                const res = await AdminService.getSFConnection();
                orgs = Array.isArray(res) ? res : [];
            }
            setDrawerOrgs(orgs);
        } catch (err) {
            console.error("[ManageQueue] Failed to load Salesforce orgs for drawer:", err);
            setDrawerOrgs([]);
        } finally {
            setDrawerLoading(false);
        }
    }, [searchTenantId]);

    /**
     * Opens the Link Salesforce Org drawer for the given queue rows.
     * @param {object[]} queues        - Selected queue row objects
     * @param {object}   [opts]
     * @param {boolean}  [opts.isSelectAll]    - True when all filtered rows are selected
     * @param {string}   [opts.filterSearch]   - Active global search string at open time
     */
    const handleLinkSalesforceOrg = useCallback(
        (queues, { isSelectAll = false, filterSearch = "" } = {}) => {
            setDrawerQueues(queues);
            setDrawerIsSelectAll(isSelectAll);
            setDrawerFilterSearch(filterSearch);
            setDrawerOpen(true);
            fetchOrgsForDrawer();
        },
        [fetchOrgsForDrawer],
    );

    /**
     * Called when the user confirms linking an org inside the drawer.
     * Calls the backend API, optimistically updates local state on success,
     * then triggers a full refetch so the table reflects the DB truth.
     *
     * @param {string}   tenantSfOrgId - TenantSfOrg UUID (from org.id in /salesforce/orgs)
     * @param {object[]} queues        - The queue rows being linked
     * @param {function} [onSuccess]   - Optional callback fired on success (used for toasts)
     * @param {function} [onError]     - Optional callback fired on error (used for toasts)
     */
    const handleLinkConfirm = useCallback(async (tenantSfOrgId, queues, { onSuccess, onError } = {}) => {
        if (!tenantSfOrgId) return;
        if (!drawerIsSelectAll && !queues?.length) return;
        setLinking(true);
        setLinkError(null);
        try {
            if (drawerIsSelectAll) {
                // Select-all path — never sends queue IDs; backend drives the UPDATE.
                // No optimistic update here because we don't know the full set of
                // affected rows; a refetch will reflect the DB truth instead.
                await AdminService.linkSalesforceOrg({
                    tenantSfOrgId,
                    selectionMode: "ALL",
                    filters: drawerFilterSearch ? { search: drawerFilterSearch } : undefined,
                    searchTenantId: searchTenantId || undefined,
                });
            } else {
                // Normal selection — optimistic update for instant feedback
                await AdminService.linkSalesforceOrg({
                    tenantSfOrgId,
                    queues: queues.map((q) => ({ queueId: q.queueId, queueName: q.queueName })),
                    searchTenantId: searchTenantId || undefined,
                });

                const linkedOrg = drawerOrgs.find((o) => o.id === tenantSfOrgId);
                setData((prev) =>
                    prev.map((q) =>
                        queues.some((sq) => sq.queueId === q.queueId)
                            ? {
                                  ...q,
                                  linkedSalesforceOrgId: linkedOrg?.orgId ?? q.linkedSalesforceOrgId,
                                  linkedSalesforceInstanceUrl:
                                      linkedOrg?.instanceUrl ?? q.linkedSalesforceInstanceUrl,
                              }
                            : q,
                    ),
                );
            }

            setRowSelection({});
        } catch (err) {
            console.error("[ManageQueue] Link failed:", err);
            setLinkError(err?.message || "Failed to link Salesforce org");
            onError?.(err);
            setLinking(false);
            return;
        }

        // Post-link steps — outside the link try/catch so that errors from
        // onSuccess (e.g. an assign-owner failure bubbling up) are NOT
        // mislabeled as link failures and do NOT prevent fetchQueues() or
        // drawer close from running.
        try {
            await onSuccess?.();
            await fetchQueues();
            setDrawerOpen(false);
        } finally {
            setLinking(false);
        }
    }, [drawerOrgs, drawerIsSelectAll, drawerFilterSearch, searchTenantId, fetchQueues, setRowSelection]);

    /** Closes the link drawer without taking any action. */
    const handleDrawerDismiss = useCallback(() => {
        setDrawerOpen(false);
    }, []);

    // ── Bulk unlink handlers ───────────────────────────────────────────────

    /**
     * Opens the bulk unlink confirmation modal.
     * Captures selection context at the moment the button is clicked.
     * @param {object[]} queues      - Selected queue row objects
     * @param {object}   [opts]
     * @param {boolean}  [opts.isSelectAll]   - True when global select-all is active
     * @param {string}   [opts.filterSearch]  - Active search string at click time
     */
    const handleBulkUnlinkRequest = useCallback(
        (queues, { isSelectAll = false, filterSearch = "" } = {}) => {
            const linkedCount = queues.filter((q) => q.linkedSalesforceOrgId).length;
            setBulkUnlinkQueueIds(queues.map((q) => q.queueId));
            setBulkUnlinkIsSelectAll(isSelectAll);
            setBulkUnlinkFilterSearch(filterSearch);
            setBulkUnlinkSelectedCount(queues.length);
            setBulkUnlinkLinkedCount(linkedCount);
            setBulkUnlinkOpen(true);
        },
        [],
    );

    /** Cancels the bulk unlink — closes the modal without doing anything. */
    const handleBulkUnlinkCancel = useCallback(() => {
        setBulkUnlinkOpen(false);
    }, []);

    /**
     * Confirmed bulk unlink — calls the API then optimistically clears org links
     * from local state so the table updates instantly without a full refetch.
     * @param {object} [callbacks]
     * @param {function} [callbacks.onSuccess]
     * @param {function} [callbacks.onError]
     */
    const handleBulkUnlinkConfirm = useCallback(async ({ onSuccess, onError } = {}) => {
        setBulkUnlinking(true);
        try {
            if (bulkUnlinkIsSelectAll) {
                await AdminService.bulkUnlinkSalesforceOrg({
                    selectionMode: "ALL",
                    filters: bulkUnlinkFilterSearch ? { search: bulkUnlinkFilterSearch } : undefined,
                    searchTenantId: searchTenantId || undefined,
                });
                // Optimistic update — clear all visible rows (org + missed-call owner)
                setData((prev) =>
                    prev.map((q) => ({
                        ...q,
                        linkedSalesforceOrgId:       null,
                        linkedSalesforceInstanceUrl: null,
                        missedCallOwnerId:           null,
                        missedCallOwnerUsername:     null,
                        missedCallOwnerSfUserId:     null,
                    })),
                );
            } else {
                await AdminService.bulkUnlinkSalesforceOrg({
                    queueIds: bulkUnlinkQueueIds,
                    searchTenantId: searchTenantId || undefined,
                });
                // Optimistic update — clear only the targeted rows (org + missed-call owner)
                const targetSet = new Set(bulkUnlinkQueueIds);
                setData((prev) =>
                    prev.map((q) =>
                        targetSet.has(q.queueId)
                            ? {
                                  ...q,
                                  linkedSalesforceOrgId:       null,
                                  linkedSalesforceInstanceUrl: null,
                                  missedCallOwnerId:           null,
                                  missedCallOwnerUsername:     null,
                                  missedCallOwnerSfUserId:     null,
                              }
                            : q,
                    ),
                );
            }
            setBulkUnlinkOpen(false);
            setRowSelectionRaw({});
            setIsGlobalSelectAll(false);
            onSuccess?.();
        } catch (err) {
            console.error("[ManageQueue] Bulk unlink failed:", err);
            onError?.(err);
        } finally {
            setBulkUnlinking(false);
        }
    }, [bulkUnlinkIsSelectAll, bulkUnlinkQueueIds, bulkUnlinkFilterSearch, searchTenantId]);

    /**
     * Sync (granular-sync) all queues from upstream sources (e.g. Microsoft Teams).
     * Calls the backend, then refetches the current page so the UI reflects
     * whatever sort / search / pagination the user has active.
     * @param {object}   [callbacks]
     * @param {function} [callbacks.onSuccess] Receives the API response
     *                    (`{ message, syncSummary, data, ... }`) so callers can
     *                    surface counts in a toast.
     * @param {function} [callbacks.onError]
     */
    const handleSyncQueues = useCallback(async ({ onSuccess, onError } = {}) => {
        setSyncing(true);
        try {
            const res = await AdminService.syncQueues(searchTenantId || undefined);
            // Immediately stamp the new lastSyncQueuesAt so the UI updates
            // without waiting for the full refetch to complete.
            if (res?.tenant) setTenantInfo(res.tenant);
            // Refetch to preserve the user's current sort / search / page.
            // The sync response itself always returns page 1 with default sort.
            await fetchQueues();
            onSuccess?.(res);
        } catch (err) {
            console.error("[ManageQueue] Sync failed:", err);
            onError?.(err);
        } finally {
            setSyncing(false);
        }
    }, [fetchQueues, searchTenantId]);

    /** Updates individual fields of a single queue row in local state without a full refetch. */
    const patchQueueData = useCallback((queueId, fields) => {
        setData((prev) => prev.map((q) => q.queueId === queueId ? { ...q, ...fields } : q));
    }, []);

    return {
        data,
        loading,
        error,
        totalCount,
        tenantInfo,
        refetch: fetchQueues,
        patchQueueData,
        sorting,
        setSorting,
        rowSelection,
        setRowSelection,
        isGlobalSelectAll,
        setIsGlobalSelectAll,
        globalFilter,
        setGlobalFilter,
        pagination,
        setPagination,
        // Unlink modal (single row)
        unlinkTarget,
        unlinking,
        handleUnlinkRequest,
        handleUnlinkConfirm,
        handleUnlinkCancel,
        // Bulk unlink modal
        bulkUnlinkOpen,
        bulkUnlinking,
        bulkUnlinkIsSelectAll,
        bulkUnlinkSelectedCount,
        bulkUnlinkLinkedCount,
        handleBulkUnlinkRequest,
        handleBulkUnlinkConfirm,
        handleBulkUnlinkCancel,
        handleLinkSalesforceOrg,
        // Drawer
        drawerOpen,
        drawerOrgs,
        drawerLoading,
        drawerQueues,
        handleDrawerDismiss,
        handleLinkConfirm,
        linking,
        linkError,
        drawerIsSelectAll,
        drawerFilterSearch,
        // Sync queues
        syncing,
        handleSyncQueues,
    };
}
